<template>
  <v-form v-if="data" class="my-2" :readonly="!editMode">
    <v-card class="mb-3">
      <v-card-title>Выпуск в ГО, закупки, ценовая характеристика</v-card-title>
      <v-card-subtitle v-if="editMode && needRefresh">
        <ReferenceTip>
          Обновить данные из экспертизы до актуальной версии можно кнопкой
          «обновить» (<v-icon color="green" small>mdi-sync</v-icon>)
        </ReferenceTip>
      </v-card-subtitle>
      <v-card-text>
        <v-container>
          <v-row dense>
            <v-col>
              <v-alert v-if="needRefresh" type="warning" dense text>
                Внимание! Информация по экспертизе изменилась, рекомендуется
                обновить данные
                <template #append>
                  <v-btn
                    v-if="editMode"
                    @click="refreshAbout"
                    :disabled="!needRefresh"
                    text
                    color="green"
                    small
                    title="Обновить все данные по экспертизе"
                    ><v-icon>mdi-sync</v-icon></v-btn
                  >
                </template>
              </v-alert>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col>
              <v-timeline dense align-top>
                <v-timeline-item small>
                  <check-alert
                    v-model="data.mnn"
                    :check-value="initMnn"
                    :hide-check="logView"
                    @change="doChange(true)"
                    label="МНН"
                  />
                </v-timeline-item>
                <v-timeline-item small>
                  <check-alert
                    v-model="data.condition"
                    :check-value="initCondition"
                    :hide-check="logView"
                    @change="doChange(true)"
                    label="Показание"
                  />
                </v-timeline-item>
                <v-timeline-item small>
                  <check-alert
                    :value="defaultList"
                    :check-value="initList"
                    :hide-check="logView"
                    @change="listOfListChange"
                    empty-value="-- не определён --"
                    label="Перечень"
                  />
                </v-timeline-item>
              </v-timeline>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <HelpCard open outlined>
                <v-expansion-panels class="my-2" flat>
                  <v-expansion-panel>
                    <v-expansion-panel-header class="text-subtitle-2">
                      Справка по загрузке Excel
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="text-body-2" eager>
                      <ul>
                        <li>
                          <strong>Основной файл</strong> — листы с именами вида
                          «Выпуск в ГО и план (ФВ-n)», где n — номер формы
                          выпуска.
                        </li>
                        <li>
                          <strong>Выпуск в ГО</strong> — отдельная книга Excel, в
                          которой есть лист с именем «Выпуск в ГО».
                        </li>
                        <li>
                          <strong>Закупки</strong> — отдельная книга Excel, в
                          которой есть лист с именем «Закупки».
                        </li>
                        <li>
                          <strong>Ценовая характеристика</strong> — отдельная
                          книга Excel, в которой есть лист с именем «Ценовая
                          характеристика».
                        </li>
                        <li>
                          Пояснения к заполнению обычно на отдельном листе
                          (например «{{ helpSheetHint }}») — в расчёт таблиц не
                          входят.
                        </li>
                      </ul>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </HelpCard>
            </v-col>
          </v-row>

          <v-row dense class="mb-1">
            <v-col cols="12" md="6" lg="4">
              <SelectDate
                v-if="editMode"
                v-model="data.analysisDate"
                :maxDate="currentDate"
                label="Дата проведения анализа"
                @input="doChange(true)"
              />
              <div v-else class="body-2">
                <span class="font-weight-medium">Дата проведения анализа: </span>
                <span class="text-decoration-underline">{{
                  data.analysisDate || '—'
                }}</span>
              </div>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <div class="text-subtitle-2 mb-2">Форма выпуска</div>
              <div
                v-for="(form, idx) in data.releaseForms"
                :key="'rf-' + idx"
                class="d-flex flex-wrap align-end mb-2"
              >
                <v-text-field
                  :value="form.name"
                  outlined
                  dense
                  hide-details
                  label="Форма выпуска"
                  :readonly="!editMode"
                  class="mr-2 mb-1"
                  style="flex: 1 1 0; min-width: 200px"
                  @input="onReleaseFormInput(idx, $event)"
                  @blur="doBlur"
                />
                <v-text-field
                  :value="form.packageCount"
                  outlined
                  dense
                  hide-details
                  type="number"
                  min="0"
                  step="1"
                  label="Количество упаковок"
                  :readonly="!editMode"
                  class="flex-shrink-0 mr-2 mb-1"
                  style="flex: 0 0 9.5rem; width: 9.5rem; max-width: 9.5rem"
                  @input="onReleaseFormPackageCountInput(idx, $event)"
                  @blur="doBlur"
                />
                <div v-if="editMode && data.releaseForms.length > 1" class="ml-2 mb-1">
                  <btn-with-confirm
                    text
                    icon
                    icon-main="mdi-delete-forever-outline"
                    title="Удалить форму выпуска"
                    @click="removeReleaseForm(idx)"
                  />
                </div>
              </div>
              <div v-if="editMode" class="d-flex pt-2 pl-0 mb-2">
                <v-btn
                  color="accent"
                  fab
                  small
                  title="Добавить форму выпуска"
                  @click="addReleaseForm"
                >
                  <v-icon> mdi-plus </v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <v-alert
                type="success"
                outlined
                dense
                class="mb-0"
              >
                <div class="text-body-2 mb-2">
                  Остатки лекарственного препарата по данным Федеральной
                  государственной информационной системы мониторинга движения
                  лекарственных препаратов для медицинского применения на
                  территории Российской Федерации (по состоянию на
                  {{ todayRuDate }}):
                </div>
                <div
                  v-for="(item, idx) in releaseFormsStockLines"
                  :key="'stock-line-' + idx"
                  class="text-body-2"
                >
                  {{ item }}
                </div>
                <div
                  v-if="!releaseFormsStockLines.length"
                  class="text-body-2 text--disabled"
                >
                  Заполните форму выпуска и количество упаковок.
                </div>
              </v-alert>
            </v-col>
          </v-row>

          <v-subheader class="px-0 text-body-1 font-weight-medium"
            >Выпуск в гражданский оборот за календарный год</v-subheader
          >
          <UpdateGoPricing
            variant="go"
            :log-view="logView"
            :local-file="data.goFile"
            :edit-mode="editMode"
            @change="doChangeFile"
            @parse-error="onParseError"
          />
          <v-alert
            v-if="parseErrors.go"
            type="error"
            outlined
            dense
            class="mt-2"
            >{{ parseErrors.go }}</v-alert
          >
          <v-card outlined class="mt-3">
            <v-card-title class="text-subtitle-1 py-2">Таблица «Выпуск в ГО»</v-card-title>
            <v-card-text>
              <GoPricingSheetGrid
                v-if="(data.goRows || []).length"
                :rows="data.goRows"
                :show-meta="false"
              />
              <span v-else class="text--disabled text-body-2"
                >Нет данных — загрузите отдельный Excel с листом «Выпуск в ГО».</span
              >
            </v-card-text>
          </v-card>

          <v-divider class="my-6" />

          <v-subheader class="px-0 text-body-1 font-weight-medium"
            >Выпуск в гражданский оборот за текущий год</v-subheader
          >
          <v-alert v-if="!releaseFormsCount" type="info" outlined dense class="mt-2">
            Не создано ни одной формы выпуска. Добавьте форму выпуска выше,
            чтобы отобразить таблицы «Выпуск в ГО и планы (ФВ-n)».
          </v-alert>
          <template v-else>
            <UpdateGoPricing
              variant="main"
              :log-view="logView"
              :local-file="data.file"
              :edit-mode="editMode"
              @change="doChangeFile"
              @parse-error="onParseError"
            />
            <v-alert
              v-if="parseErrors.main"
              type="error"
              outlined
              dense
              class="mt-2"
              >{{ parseErrors.main }}</v-alert
            >

            <v-row dense class="mt-2">
              <v-col>
                <v-tabs v-model="tabMain" show-arrows>
                  <v-tab
                    v-for="idx in releaseFormsCount"
                    :key="'tab-fv-' + idx"
                    class="font-weight-bold"
                  >
                    <span v-if="mainSheetByForm[idx - 1]">
                      ФВ {{ idx }}
                    </span>
                    <span v-else class="error--text">ФВ {{ idx }}</span>
                  </v-tab>
                </v-tabs>
                <v-tabs-items v-model="tabMain">
                  <v-tab-item
                    v-for="idx in releaseFormsCount"
                    :key="'main-fv-' + idx"
                    class="pt-3"
                    eager
                  >
                    <template v-if="mainSheetByForm[idx - 1]">
                      <div class="text-subtitle-2 mb-2">
                        {{ mainSheetByForm[idx - 1].name }}
                      </div>
                      <GoPricingSheetGrid :rows="mainSheetByForm[idx - 1].rows" />
                    </template>
                    <v-alert v-else type="error" outlined>
                      Информация для формы выпуска {{ idx }} у «Выпуск в ГО и
                      планы (ФВ-n)» отсутствует в документе.
                    </v-alert>
                  </v-tab-item>
                </v-tabs-items>
              </v-col>
            </v-row>
          </template>

          <v-divider class="my-6" />

          <v-subheader class="px-0 text-body-1 font-weight-medium"
            >Закупки</v-subheader
          >
          <UpdateGoPricing
            variant="procurement"
            :log-view="logView"
            :local-file="data.procurementFile"
            :edit-mode="editMode"
            @change="doChangeFile"
            @parse-error="onParseError"
          />
          <v-alert
            v-if="parseErrors.procurement"
            type="error"
            outlined
            dense
            class="mt-2"
            >{{ parseErrors.procurement }}</v-alert
          >
          <v-card outlined class="mt-3">
            <v-card-title class="text-subtitle-1 py-2">Таблица «Закупки»</v-card-title>
            <v-card-text>
              <div
                v-if="(data.procurementRows || []).length"
                class="text-subtitle-2 font-weight-medium mb-3"
              >
                Закупки предлагаемого ЛП (в соответствии с данными
                информационного ресурса «ИАС Закупки»)
              </div>
              <GoPricingSheetGrid
                v-if="(data.procurementRows || []).length"
                variant="procurement"
                :rows="data.procurementRows"
                :show-meta="false"
              />
              <span v-else class="text--disabled text-body-2"
                >Нет данных — загрузите отдельный Excel с листом «Закупки».</span
              >
            </v-card-text>
          </v-card>

          <v-divider class="my-6" />

          <v-subheader class="px-0 text-body-1 font-weight-medium"
            >Ценовая характеристика</v-subheader
          >
          <UpdateGoPricing
            variant="pricing"
            :log-view="logView"
            :local-file="data.pricingFile"
            :edit-mode="editMode"
            @change="doChangeFile"
            @parse-error="onParseError"
          />
          <v-alert
            v-if="parseErrors.pricing"
            type="error"
            outlined
            dense
            class="mt-2"
            >{{ parseErrors.pricing }}</v-alert
          >
          <v-card outlined class="mt-3">
            <v-card-title class="text-subtitle-1 py-2"
              >Таблица «Ценовая характеристика»</v-card-title
            >
            <v-card-text>
              <div
                v-if="(data.pricingRows || []).length"
                class="text-subtitle-2 font-weight-medium mb-3"
              >
                Ценовая характеристика:
              </div>
              <GoPricingSheetGrid
                v-if="(data.pricingRows || []).length"
                variant="pricing"
                :rows="data.pricingRows"
                :show-meta="false"
              />
              <span v-else class="text--disabled text-body-2"
                >Нет данных — загрузите отдельный Excel с листом «Ценовая
                характеристика».</span
              >
            </v-card-text>
          </v-card>
        </v-container>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script>
import { copyObject } from '@/lib/objects'
import {
  EX_GO_PRICING_TEMPLATE,
  GO_PRICING_HELP_SHEET_HINT,
} from '@/components/expGoPricing/const'
import UpdateGoPricing from '@/components/expGoPricing/UpdateGoPricing'
import GoPricingSheetGrid from '@/components/expGoPricing/GoPricingSheetGrid'
import { SUB_EX_ABOUT } from '@/store/const/expertise'
import { mapActions, mapMutations } from 'vuex'
import ReferenceTip from '@/components/ReferenceTip'
import CheckAlert from '@/components/CheckAlert'
import HelpCard from '@/components/HelpCard'
import SelectDate from '@/components/editors/SelectDate'
import btnWithConfirm from '@/components/buttons/btnWithConfirm'

export default {
  name: 'GoPricingCalc',
  components: {
    HelpCard,
    CheckAlert,
    ReferenceTip,
    SelectDate,
    btnWithConfirm,
    UpdateGoPricing,
    GoPricingSheetGrid,
  },
  data: () => ({
    data: null,
    loading: false,
    initVzn14: false,
    initGnvlp: false,
    initMnn: '',
    initCondition: '',
    error: '',
    parseErrors: {
      main: '',
      go: '',
      procurement: '',
      pricing: '',
    },
    tabMain: 0,
  }),
  props: {
    value: {
      type: Object,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    logView: {
      type: Boolean,
      default: false,
    },
    onCreate: {
      type: Function,
      default: null,
    },
  },
  computed: {
    helpSheetHint() {
      return GO_PRICING_HELP_SHEET_HINT
    },
    editMode() {
      return this.value && !this.readonly && !this.logView
    },
    needRefresh() {
      return !this.logView && !this.loading && this.showAlert
    },
    showAlert() {
      return !this.checkData
    },
    checkData() {
      return this.checkMnn && this.checkCondition && this.checkList
    },
    checkMnn() {
      return this.initMnn === this.data?.mnn
    },
    checkCondition() {
      return this.initCondition === this.data?.condition
    },
    checkList() {
      return this.defaultList === this.initList
    },
    initList() {
      const list = []
      if (this.initGnvlp) list.push('ЖНВЛП')
      if (this.initVzn14) list.push('ВЗН14')
      return list.join(', ')
    },
    defaultList() {
      const list = []
      if (this.data?.gnvlp) list.push('ЖНВЛП')
      if (this.data?.vzn14) list.push('ВЗН14')
      return list.join(', ')
    },
    releaseFormsCount() {
      const forms = Array.isArray(this.data?.releaseForms)
        ? this.data.releaseForms
        : []
      return forms.filter(v => String(v?.name || '').trim()).length
    },
    mainSheetByForm() {
      const out = []
      const list = Array.isArray(this.data?.sheets) ? this.data.sheets : []
      for (let i = 0; i < list.length; i++) {
        const sh = list[i]
        const m = String(sh?.name || '').match(/\(ФВ-(\d+)\)$/i)
        if (!m) continue
        const idx = Number(m[1]) - 1
        if (idx >= 0) out[idx] = sh
      }
      return out
    },
    currentDate() {
      return String(new Date().toISOString().slice(0, 10))
    },
    todayRuDate() {
      return new Date().toLocaleDateString('ru-RU')
    },
    releaseFormsStockLines() {
      const forms = Array.isArray(this.data?.releaseForms)
        ? this.data.releaseForms
        : []
      return forms
        .filter(f => String(f?.name || '').trim() && Number.isFinite(Number(f?.packageCount)))
        .map(f => `${String(f.name).trim()} - ${Number(f.packageCount)} уп.`)
    },
  },
  watch: {
    value(newElement) {
      if (this.readonly) this.data = this.value
      else this.data = copyObject(newElement)
    },
  },
  async created() {
    this.loading = true
    try {
      if (!this.logView) await this.loadAbout()

      if (this.readonly) this.data = this.value
      else this.data = copyObject(this.value)
      if (!this.data && this.onCreate) {
        this.data = await this.onCreate(await this.initNewData())
      }
      this.ensurePricingShape()
    } finally {
      this.loading = false
    }
  },
  mounted() {
    this.reInit()
  },
  methods: {
    ...mapActions(['GET_PLP_REPORT']),
    ...mapMutations(['SET_ERROR']),
    ensurePricingShape() {
      if (!this.data) return
      if (
        this.data.analysisDate === undefined ||
        this.data.analysisDate === null
      ) {
        this.$set(this.data, 'analysisDate', '')
      }
      if (
        !Array.isArray(this.data.releaseForms) ||
        !this.data.releaseForms.length
      ) {
        this.$set(this.data, 'releaseForms', [{ name: '', packageCount: null }])
      } else {
        this.$set(
          this.data,
          'releaseForms',
          this.data.releaseForms.map(item =>
            typeof item === 'object' && item !== null
              ? {
                  name: item.name ?? '',
                  packageCount:
                    item.packageCount === '' || item.packageCount === null
                      ? null
                      : Number(item.packageCount),
                }
              : { name: String(item || ''), packageCount: null }
          )
        )
      }
      if (!Array.isArray(this.data.procurementRows)) {
        this.$set(this.data, 'procurementRows', [])
      }
      if (!Array.isArray(this.data.pricingRows)) {
        this.$set(this.data, 'pricingRows', [])
      }
      if (!Array.isArray(this.data.goRows)) {
        this.$set(this.data, 'goRows', [])
      }
      if (
        (!this.data.goRows || !this.data.goRows.length) &&
        Array.isArray(this.data.sheets)
      ) {
        const goSheet = this.data.sheets.find(s => s.name === 'Выпуск в ГО')
        if (goSheet?.rows?.length) {
          this.$set(this.data, 'goRows', copyObject(goSheet.rows))
        }
      }
      if (!this.data.procurementFile) {
        this.$set(this.data, 'procurementFile', {
          uuid: '',
          filename: '',
          size: 0,
          data: {},
          time_create: '',
        })
      }
      if (!this.data.pricingFile) {
        this.$set(this.data, 'pricingFile', {
          uuid: '',
          filename: '',
          size: 0,
          data: {},
          time_create: '',
        })
      }
      if (!this.data.goFile) {
        this.$set(this.data, 'goFile', {
          uuid: '',
          filename: '',
          size: 0,
          data: {},
          time_create: '',
        })
      }
      if (this.tabMain >= this.releaseFormsCount) {
        this.tabMain = 0
      }
    },
    shortTabTitle(name) {
      if (!name) return ''
      return name.length > 28 ? `${name.slice(0, 26)}…` : name
    },
    doChange(force = false) {
      if (!this.readonly) this.$emit('change', this.data, force)
    },
    doChangeFile(payload) {
      const k = payload.kind
      this.parseErrors = { ...this.parseErrors, [k]: '' }
      if (k === 'main') {
        if (Array.isArray(payload.sheets))
          this.data.sheets = copyObject(payload.sheets)
        if (payload.localFile) this.data.file = payload.localFile
      } else if (k === 'go') {
        if (Array.isArray(payload.rows)) this.data.goRows = copyObject(payload.rows)
        if (payload.localFile) this.data.goFile = payload.localFile
      } else if (k === 'procurement') {
        if (Array.isArray(payload.rows))
          this.data.procurementRows = copyObject(payload.rows)
        if (payload.localFile) this.data.procurementFile = payload.localFile
      } else if (k === 'pricing') {
        if (Array.isArray(payload.rows))
          this.data.pricingRows = copyObject(payload.rows)
        if (payload.localFile) this.data.pricingFile = payload.localFile
      }
      this.doChange(true)
    },
    onParseError({ variant, message }) {
      this.parseErrors = { ...this.parseErrors, [variant]: message || '' }
    },
    async refreshAbout() {
      this.data.mnn = this.initMnn || ''
      this.data.condition = this.initCondition || ''
      this.data.gnvlp = this.initGnvlp || false
      this.data.vzn14 = this.initVzn14 || false
      this.doChange(true)
    },
    doBlur() {
      this.$emit('blur')
    },
    reInit() {
      if (this.readonly) {
        this.data = this.value
      }
      this.ensurePricingShape()
    },
    async initNewData() {
      try {
        const resData = await this.getExpertiseData()
        resData.mnn = this.initMnn || ''
        resData.condition = this.initCondition || ''
        resData.vzn14 = this.initVzn14 || false
        resData.gnvlp = this.initGnvlp || false
        return resData
      } catch (err) {
        this.SET_ERROR({
          head: 'Выпуск в ГО, закупки, ценовая характеристика',
          text: 'Ошибка загрузки данных',
          err,
        })
      }
    },
    async getExpertiseData() {
      try {
        return copyObject(EX_GO_PRICING_TEMPLATE())
      } catch (err) {
        this.SET_ERROR({
          head: 'Выпуск в ГО, закупки, ценовая характеристика',
          text: 'Ошибка загрузки шаблона',
          err,
        })
      }
    },
    async loadAbout() {
      this.loading = true
      try {
        const { expertise } = await this.GET_PLP_REPORT([SUB_EX_ABOUT])
        const about = expertise?.about || {}
        this.initGnvlp = about.gnvlp || false
        this.initVzn14 = about.vzn14 || false
        this.initMnn = about.mnn || ''
        this.initCondition = about.condition || ''
      } catch (err) {
        this.error = err.message + (err.error ? ' ' + err.error : '')
        throw err
      } finally {
        this.loading = false
      }
    },
    listOfListChange() {
      this.data.gnvlp = this.initGnvlp || false
      this.data.vzn14 = this.initVzn14 || false
      this.doChange(true)
    },
    onReleaseFormInput(idx, val) {
      this.$set(this.data.releaseForms[idx], 'name', val)
      this.doChange(false)
    },
    onReleaseFormPackageCountInput(idx, val) {
      const num = val === '' || val === null ? null : Number(val)
      this.$set(
        this.data.releaseForms[idx],
        'packageCount',
        Number.isFinite(num) ? num : null
      )
      this.doChange(false)
    },
    addReleaseForm() {
      this.data.releaseForms.push({ name: '', packageCount: null })
      this.doChange(true)
    },
    removeReleaseForm(idx) {
      this.data.releaseForms.splice(idx, 1)
      if (!this.data.releaseForms.length) {
        this.data.releaseForms.push({ name: '', packageCount: null })
      }
      this.doChange(true)
    },
  },
}
</script>
